import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, peso } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CustomerLayout } from "@/components/CustomerLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardList, MapPin, User, ShoppingBag, Soup } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ORDER = ["Family Meals", "Pair Meal", "Solo Meal", "Drinks", "MisterMic Essentials"];

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const cats = ["All", ...CATEGORY_ORDER.filter((c) => products.some((p) => p.category === c))];
  const shown = active === "All" ? products : products.filter((p) => p.category === active);

  const scrollMenu = () => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });

  return (
    <CustomerLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1B5E20] text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFB300 0, transparent 40%), radial-gradient(circle at 80% 70%, #C62828 0, transparent 45%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <Badge className="bg-[#FFB300] text-[#1A251C] hover:bg-[#FFB300] mb-4 font-bold tracking-wide">CANYON RANCH · CARMONA, CAVITE</Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-2xl">
            Hot, homemade <span className="text-[#FFB300]">sabaw</span> delivered to your door.
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-xl">GOOD SOUP. GOOD DAY. Fresh Bulalo, Sinigang & Tinola plus MisterMic Essentials — one cart, one delivery.</p>
          <div className="mt-8 grid grid-cols-2 sm:flex gap-3 max-w-lg">
            <Button onClick={scrollMenu} data-testid="order-now-btn" className="bg-[#C62828] hover:bg-[#B71C1C] h-14 text-base font-bold rounded-xl col-span-2 sm:col-auto px-8">
              <ShoppingBag className="mr-1" size={20} /> ORDER NOW
            </Button>
            <Button onClick={() => nav(user ? "/my-orders" : "/login")} data-testid="hero-orders-btn" variant="outline" className="h-14 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-xl font-semibold">
              <ClipboardList size={18} className="mr-1" /> My Orders
            </Button>
            <Button onClick={() => nav("/track")} data-testid="hero-track-btn" variant="outline" className="h-14 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-xl font-semibold">
              <MapPin size={18} className="mr-1" /> Track Order
            </Button>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Soup className="text-[#1B5E20]" /> <h2 className="font-display text-2xl sm:text-3xl font-bold">Our Menu</h2>
        </div>
        <p className="text-muted-foreground mb-6">Choose a category and add your favorites to the cart.</p>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1">
          {cats.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${active === c ? "bg-[#1B5E20] text-white border-[#1B5E20]" : "bg-white text-[#1B5E20] border-[#1B5E20]/20 hover:border-[#1B5E20]"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center py-10 text-muted-foreground">Loading menu…</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {shown.map((p) => (
              <div key={p.id} className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-lg transition-shadow flex flex-col" data-testid={`product-card-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : <div className="h-full grid place-items-center text-muted-foreground"><Soup /></div>}
                  {p.stock_status === "low" && <Badge className="absolute top-2 left-2 bg-[#FFB300] text-[#1A251C]">Low stock</Badge>}
                  {p.featured && <Badge className="absolute top-2 right-2 bg-[#C62828]">Bestseller</Badge>}
                </div>
                <div className="p-3 sm:p-4 flex flex-col flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#B78103]">{p.category}</div>
                  <h3 className="font-semibold text-sm sm:text-base leading-tight mt-0.5">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-display font-extrabold text-lg text-[#1B5E20]">{peso(p.price)}</span>
                    <Button size="sm" onClick={() => { add(p); toast.success(`${p.name} added to cart`); }} data-testid={`add-btn-${p.id}`}
                      className="bg-[#1B5E20] hover:bg-[#144718] rounded-lg h-9">
                      <Plus size={16} className="mr-0.5" /> Add
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </CustomerLayout>
  );
}
